import React, { useState, useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import FamilyActions from './family/FamilyActions';
import type { FamilyToolsMember } from './family/types';
import SetMemberPasswordDialog from './SetMemberPasswordDialog';
import { isPlaceholderEmail } from '@shared/householdRenewal';

const client = generateClient({ authMode: 'userPool' });

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address: string;
  suburb?: string;
  state?: string;
  postCode?: string;
  [key: string]: any;
}

interface EditMemberModalProps {
  member: Member;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedMember: Member) => void;
  /** Human-readable name for this member's family (e.g. "Day Family"), resolved by the caller. */
  familyLabel?: string;
  /**
   * The full member roster, needed by the family tools (Add to a family,
   * Group into a family) to search for other members. Optional so this
   * modal still renders without it; the family actions block only appears
   * once it's supplied.
   */
  allMembers?: FamilyToolsMember[];
  /** Called after a family-tools action (Add family member, Group, Make primary, Remove) completes, to refresh the roster. */
  onFamilyChanged?: (message: string) => void;
}

interface EditableFields {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address: string;
  suburb: string;
  state: string;
  postCode: string;
  familyGroupId: string;
  isCommittee: boolean;
}

const validateEditFields = (fields: EditableFields): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!fields.firstName.trim()) errors.firstName = 'First name is required';
  if (!fields.lastName.trim()) errors.lastName = 'Last name is required';
  if (!fields.email.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) {
    errors.email = 'Invalid email format';
  }
  if (!fields.phoneNumber.trim()) errors.phoneNumber = 'Phone number is required';
  if (!fields.address.trim()) errors.address = 'Address is required';
  return errors;
};

const EditMemberModal: React.FC<EditMemberModalProps> = ({
  member,
  isOpen,
  onClose,
  onSave,
  familyLabel,
  allMembers,
  onFamilyChanged,
}) => {
  const [fields, setFields] = useState<EditableFields>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    suburb: '',
    state: '',
    postCode: '',
    familyGroupId: '',
    isCommittee: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  // #136: "Set password now" (the member types it on this device).
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const setPasswordButtonRef = useRef<HTMLButtonElement>(null);
  // #150 slice 2 (#58 checklist) — collapsed by default so the modal isn't
  // longer for members who already have a login.
  const [loginChecklistOpen, setLoginChecklistOpen] = useState(false);
  // Only for members with a portal account and a real email on record.
  // Admin accounts are refused by the server (the modal doesn't know Cognito groups).
  const canSetPassword = !!member.cognitoSub && !!member.email && !isPlaceholderEmail(member.email);
  const closeSetPassword = () => {
    setSetPasswordOpen(false);
    // Return focus to the button that opened the dialog.
    setTimeout(() => setPasswordButtonRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (isOpen && member) {
      setFields({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        email: member.email || '',
        phoneNumber: member.phoneNumber || '',
        address: member.address || '',
        suburb: (member as any).suburb || '',
        state: (member as any).state || '',
        postCode: (member as any).postCode || '',
        familyGroupId: (member as any).familyGroupId || '',
        isCommittee: (member as any).isCommittee || false,
      });
      setErrors({});
      setApiError(null);
    }
  }, [isOpen, member]);

  if (!isOpen) return null;

  const handleChange = (field: keyof EditableFields, value: string) => {
    setFields((prev) => ({ ...prev, [field]: value }));
    if (errors[field])
      setErrors((prev) => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
  };

  const handleSave = async () => {
    const validationErrors = validateEditFields(fields);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    setApiError(null);

    try {
      const emailChanged = fields.email.trim().toLowerCase() !== (member.email || '').trim().toLowerCase();

      // Email changes must ALSO sync the member's Cognito login identity —
      // updateMemberEmail (added 16 Aug 2026) handles that side effect.
      // Plain Member.update() below (unchanged) only ever writes the
      // DynamoDB record; before this, an admin correcting a typo'd email
      // here silently never touched the member's actual Cognito sign-in
      // email at all, leaving them unable to receive password resets at
      // their real address. Run this FIRST so a rejection (e.g. the new
      // email is already claimed by a different Cognito account) is
      // surfaced before any DynamoDB write happens, avoiding a
      // Member.email that no longer matches what the member can actually
      // log in with.
      if (emailChanged) {
        const emailResult: any = await (client.mutations as any).updateMemberEmail({
          memberId: member.id,
          newEmail: fields.email.trim(),
        });
        const emailData = parseLambdaResponse<{ success?: boolean; error?: string }>(emailResult?.data);
        if (!emailData?.success) {
          throw new Error(emailData?.error || 'Failed to update email.');
        }
      }

      const result = await client.models.Member.update({
        id: member.id,
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        email: fields.email.trim(),
        phoneNumber: fields.phoneNumber.trim(),
        address: fields.address.trim(),
        suburb: fields.suburb.trim() || undefined,
        state: fields.state.trim() || undefined,
        postCode: fields.postCode.trim() || undefined,
        familyGroupId: fields.familyGroupId.trim() || null,
        isCommittee: fields.isCommittee,
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      onSave({ ...member, ...fields } as Member);
    } catch (err: any) {
      console.error('Error updating member:', err);
      setApiError(err.message || 'Failed to update member. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (
    label: string,
    field: keyof Omit<EditableFields, 'isCommittee'>,
    required = false,
    type = 'text'
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={fields[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue ${
          errors[field] ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {errors[field] && <p className="text-red-500 text-sm mt-1">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-member-modal-title"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !saving) onClose();
          }}
          className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
        >
          <h3 id="edit-member-modal-title" className="text-lg font-bold text-gray-900 mb-4">
            Edit Member: {member.firstName} {member.lastName}
          </h3>

          {apiError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {apiError}
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {renderField('First Name', 'firstName', true)}
              {renderField('Last Name', 'lastName', true)}
            </div>
            {renderField('Email', 'email', true, 'email')}
            {renderField('Phone Number', 'phoneNumber', true, 'tel')}
            {renderField('Address', 'address', true)}
            <div className="grid grid-cols-3 gap-4">
              {renderField('Suburb', 'suburb')}
              {renderField('State', 'state')}
              {renderField('Post Code', 'postCode')}
            </div>

            {/* Family group fields (admin only) */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Family Group
              </p>
              {/* Read-only by design. This is the internal foreign key that links family
                  members; a typo here silently orphans a member from their family or
                  merges them into the wrong one, with no error. Use the family actions
                  below (Add to a family, Group into a family, Remove from family) instead
                  of editing this UUID directly. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Family</label>
                {fields.familyGroupId ? (
                  <>
                    <p className="text-sm text-gray-900">
                      {familyLabel ?? 'Linked to a family group'}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="text-xs text-gray-500 break-all">
                        {fields.familyGroupId}
                      </code>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(fields.familyGroupId)}
                        className="text-xs text-fishing-blue hover:underline whitespace-nowrap"
                        title="Copy the internal family group ID"
                      >
                        Copy ID
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Read-only. Use the family actions below to change it.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">Not part of a family group</p>
                )}
              </div>
            </div>

            {allMembers && (
              <FamilyActions
                member={member as any}
                allMembers={allMembers}
                onChanged={(message) => {
                  onFamilyChanged?.(message);
                }}
              />
            )}

            {/* #150 slice 2 (#58 checklist) — only for a member with no
                login yet. Steps in words; the actual action (Grant portal
                access) lives in MemberManagement's row actions, reached
                from there once this modal is closed. */}
            {!member.cognitoSub && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <button
                  type="button"
                  onClick={() => setLoginChecklistOpen((v) => !v)}
                  aria-expanded={loginChecklistOpen}
                  aria-controls="give-login-checklist"
                  className="min-h-[44px] flex w-full items-center justify-between text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                >
                  <span>Give this person a login</span>
                  <span aria-hidden="true">{loginChecklistOpen ? '▲' : '▼'}</span>
                </button>
                {loginChecklistOpen && (
                  <ol id="give-login-checklist" className="mt-3 list-decimal space-y-2 pl-5 text-sm text-gray-700">
                    <li>Make sure they have their own real email (not a placeholder).</li>
                    <li>
                      Use Grant portal access, or ask the household primary to request it from their
                      dashboard.
                    </li>
                    <li>They&rsquo;ll get an email with a temporary password.</li>
                  </ol>
                )}
              </div>
            )}


            {/* Committee flag (admin only) */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Role
              </p>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={fields.isCommittee}
                  onChange={(e) =>
                    setFields((prev) => ({ ...prev, isCommittee: e.target.checked }))
                  }
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Committee member
              </label>
              <p className="text-xs text-gray-400 mt-1">
                Adds a ⭐ Committee badge on the member's digital card.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  if (!fields.email) return;
                  setResetMsg(null);
                  setResetting(true);
                  try {
                    const res: any = await (client.mutations as any).resetMemberPassword({
                      email: fields.email,
                    });
                    const d = parseLambdaResponse(res?.data);
                    setResetMsg(
                      d?.success
                        ? d?.message || 'Password reset email sent.'
                        : d?.error || 'Reset failed.'
                    );
                  } catch (e: any) {
                    setResetMsg(e?.message || 'Reset failed.');
                  } finally {
                    setResetting(false);
                  }
                }}
                disabled={saving || resetting || !fields.email}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium disabled:bg-gray-100 disabled:text-gray-400"
              >
                {resetting ? 'Sending…' : '🔑 Send password-reset email'}
              </button>
              {canSetPassword && (
                <button
                  ref={setPasswordButtonRef}
                  type="button"
                  onClick={() => {
                    setResetMsg(null);
                    setSetPasswordOpen(true);
                  }}
                  disabled={saving || resetting}
                  className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Set password now
                </button>
              )}
              {resetMsg && <span role="status" className="text-xs text-gray-600">{resetMsg}</span>}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-fishing-blue hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:bg-gray-300"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {setPasswordOpen && (
        <SetMemberPasswordDialog
          email={member.email}
          displayName={`${member.firstName} ${member.lastName}`}
          onClose={closeSetPassword}
          onDone={(message) => {
            setResetMsg(message);
            closeSetPassword();
          }}
        />
      )}
    </div>
  );
};

export default EditMemberModal;
